// Hercules — Avrupa'da sınırsız konuşma & SMS + veri · /esim/hercules
//
// Neden kendi sayfası: Hercules hem bölgesel paketlerden (onlarda konuşma yok)
// hem de 37 tek-ülke ses paketinden farklı; ses sekmesinin içinde kaybolan bir
// amiral gemisi ürün. Bkz. docs/hercules-sayfasi.md
//
// Ayrım `scope` ile yapılır, BAŞLIKLA DEĞİL: yarın ikinci bir bölgesel ses
// paketi eklenirse liste kendiliğinden doğru davranır. Bugün tek kayıt olduğu
// için ilk kayıt gösterilir; ürün pasife alınırsa sayfa "bulunamadı" durumuna
// düşer, kırılmaz.

import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faGlobe,
  faSpinner,
  faTriangleExclamation,
  faPhone,
  faCommentSms,
  faBolt,
} from '@fortawesome/free-solid-svg-icons';
import StoreHeader from '../../components/StoreHeader';
import Footer from '../../components/Footer';
import AuthPromptModal from '../../components/AuthPromptModal';
import { useAuth } from '../../context/AuthContext';
import { formatDataOrUnlimited } from '../../utils/formatters';
import { sortedPlans } from './bundleCatalog';
import { loadRegionalVoiceBundles, formatAllowance } from './voicePlans';
import VoicePlanList from './VoicePlanList';
import './home.css';

const Feature = ({ icon, label, value }) => (
  <div className="feature">
    <FontAwesomeIcon icon={icon} className="feature-icon" />
    <div>
      <span className="feature-label">{label}</span>
      <span className="feature-value">{value}</span>
    </div>
  </div>
);

const HerculesPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [state, setState] = useState({ loading: true });
  const [authPromptOpen, setAuthPromptOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true });
    loadRegionalVoiceBundles()
      .then((list) => {
        if (cancelled) return;
        const bundle = list[0];
        setState(bundle ? { bundle } : { error: t('detail.empty') });
      })
      .catch((err) => !cancelled && setState({ error: err.message }));
    return () => {
      cancelled = true;
    };
    // Dil değişince katalog yeniden çekilir (ülke adları Accept-Language'e bağlı).
  }, [i18n.language, t]);

  const bundle = state.bundle;
  const plans = sortedPlans(bundle);
  const countryNames = (bundle?.countries || [])
    .map((c) => c.nameSelected || c.name)
    .filter(Boolean);

  // Konuşma/SMS hakkı bundle genelinde aynı; ilk plandan okunur.
  const voiceLabel = formatAllowance(t, plans[0]?.voiceMinutes, 'voice');
  const smsLabel = formatAllowance(t, plans[0]?.smsCount, 'sms');

  const buy = (plan) => {
    if (!isAuthenticated) {
      setAuthPromptOpen(true);
      return;
    }
    navigate('/checkout', {
      state: {
        productId: String(plan.id),
        priceId: plan.priceId,
        plan: {
          countryName: bundle.title,
          dataAmount: formatDataOrUnlimited(t, plan.isUnlimited, plan.dataMb),
          validity: String(plan.validityDays),
          flag: bundle.imgUrl,
          // Tahsil edilen gerçek tutar versionedPriceEur'dur (PayPal, EUR).
          priceEuro: plan.versionedPriceEur || plan.priceEur,
        },
      },
    });
  };

  return (
    <div className="home">
      <StoreHeader />

      <section className="home-catalog detail">
        <Link to="/" className="esim-detail-back">
          <FontAwesomeIcon icon={faArrowLeft} />
          {t('home.detail.back')}
        </Link>

        <header className="detail-head">
          {bundle?.imgUrl ? (
            <img className="detail-flag" src={bundle.imgUrl} alt="" />
          ) : (
            <span className="detail-flag detail-flag--fallback">
              <FontAwesomeIcon icon={faGlobe} />
            </span>
          )}
          <div>
            <h1 className="detail-title">
              {bundle?.title || 'Hercules'}
              {bundle && <span className="detail-badge">{t('detail.voice')}</span>}
            </h1>
            {countryNames.length > 0 && (
              <p className="detail-sub">
                {t('hercules.subtitle', { n: countryNames.length })}
              </p>
            )}
          </div>
        </header>

        {state.loading && (
          <div className="home-state">
            <FontAwesomeIcon icon={faSpinner} spin size="2x" />
          </div>
        )}

        {state.error && (
          <div className="home-state">
            <FontAwesomeIcon icon={faTriangleExclamation} size="2x" />
            <p>{state.error}</p>
          </div>
        )}

        {bundle && (
          <>
            <p className="detail-notice">{t('detail.voiceNote')}</p>

            {plans.length === 0 ? (
              <div className="home-state">
                <p>{t('detail.empty')}</p>
              </div>
            ) : (
              <VoicePlanList plans={plans} onBuy={buy} />
            )}

            <h2 className="plan-section-title">{t('detail.basicFeatures')}</h2>
            <div className="plan-features">
              {voiceLabel && (
                <Feature icon={faPhone} label={t('detail.calls')} value={voiceLabel} />
              )}
              {smsLabel && (
                <Feature icon={faCommentSms} label={t('detail.sms')} value={smsLabel} />
              )}
              <Feature
                icon={faBolt}
                label={t('detail.activationMethod')}
                value={t('detail.esimActivation')}
              />
            </div>

            {/* Kapsam listesi sayfanın en güçlü satış argümanı: düz metin yerine
                okunur bir çip ızgarası. */}
            {countryNames.length > 0 && (
              <>
                <h2 className="plan-section-title">
                  {t('hercules.coverageTitle', { n: countryNames.length })}
                </h2>
                <ul className="country-chips">
                  {countryNames.map((name) => (
                    <li key={name} className="country-chip">
                      {name}
                    </li>
                  ))}
                </ul>
              </>
            )}

            {bundle.description && (
              <>
                <h2 className="plan-section-title">{t('detail.description')}</h2>
                <p className="plan-description">{bundle.description}</p>
              </>
            )}
          </>
        )}
      </section>

      <Footer />

      <AuthPromptModal open={authPromptOpen} onClose={() => setAuthPromptOpen(false)} />
    </div>
  );
};

export default HerculesPage;
